// Curated palette for nearest-match color naming (CSS extended color keywords).
// Matching is done in RGB space using Euclidean distance — an approximation,
// not an exact/branded name.
const NAMED_COLORS: Array<[name: string, hex: string]> = [
  ["Black", "#000000"],
  ["Dim Gray", "#696969"],
  ["Gray", "#808080"],
  ["Dark Gray", "#a9a9a9"],
  ["Silver", "#c0c0c0"],
  ["Light Gray", "#d3d3d3"],
  ["Gainsboro", "#dcdcdc"],
  ["White Smoke", "#f5f5f5"],
  ["White", "#ffffff"],
  ["Rosy Brown", "#bc8f8f"],
  ["Indian Red", "#cd5c5c"],
  ["Brown", "#a52a2a"],
  ["Firebrick", "#b22222"],
  ["Maroon", "#800000"],
  ["Dark Red", "#8b0000"],
  ["Red", "#ff0000"],
  ["Tomato", "#ff6347"],
  ["Coral", "#ff7f50"],
  ["Orange Red", "#ff4500"],
  ["Rust", "#b7410e"],
  ["Sienna", "#a0522d"],
  ["Chocolate", "#d2691e"],
  ["Saddle Brown", "#8b4513"],
  ["Sandy Brown", "#f4a460"],
  ["Peru", "#cd853f"],
  ["Burlywood", "#deb887"],
  ["Tan", "#d2b48c"],
  ["Dark Orange", "#ff8c00"],
  ["Orange", "#ffa500"],
  ["Gold", "#ffd700"],
  ["Dark Goldenrod", "#b8860b"],
  ["Goldenrod", "#daa520"],
  ["Khaki", "#f0e68c"],
  ["Dark Khaki", "#bdb76b"],
  ["Olive", "#808000"],
  ["Yellow", "#ffff00"],
  ["Light Yellow", "#ffffe0"],
  ["Lemon Chiffon", "#fffacd"],
  ["Yellow Green", "#9acd32"],
  ["Olive Drab", "#6b8e23"],
  ["Dark Olive Green", "#556b2f"],
  ["Green Yellow", "#adff2f"],
  ["Chartreuse", "#7fff00"],
  ["Lawn Green", "#7cfc00"],
  ["Dark Green", "#006400"],
  ["Green", "#008000"],
  ["Forest Green", "#228b22"],
  ["Lime", "#00ff00"],
  ["Lime Green", "#32cd32"],
  ["Light Green", "#90ee90"],
  ["Pale Green", "#98fb98"],
  ["Dark Sea Green", "#8fbc8f"],
  ["Medium Sea Green", "#3cb371"],
  ["Sea Green", "#2e8b57"],
  ["Spring Green", "#00ff7f"],
  ["Emerald", "#50c878"],
  ["Teal", "#008080"],
  ["Dark Cyan", "#008b8b"],
  ["Light Sea Green", "#20b2aa"],
  ["Turquoise", "#40e0d0"],
  ["Medium Turquoise", "#48d1cc"],
  ["Aquamarine", "#7fffd4"],
  ["Cyan", "#00ffff"],
  ["Light Cyan", "#e0ffff"],
  ["Cadet Blue", "#5f9ea0"],
  ["Steel Blue", "#4682b4"],
  ["Sky Blue", "#87ceeb"],
  ["Light Sky Blue", "#87cefa"],
  ["Deep Sky Blue", "#00bfff"],
  ["Dodger Blue", "#1e90ff"],
  ["Cornflower Blue", "#6495ed"],
  ["Royal Blue", "#4169e1"],
  ["Blue", "#0000ff"],
  ["Medium Blue", "#0000cd"],
  ["Dark Blue", "#00008b"],
  ["Navy", "#000080"],
  ["Midnight Blue", "#191970"],
  ["Slate Blue", "#6a5acd"],
  ["Dark Slate Blue", "#483d8b"],
  ["Medium Slate Blue", "#7b68ee"],
  ["Indigo", "#4b0082"],
  ["Purple", "#800080"],
  ["Dark Violet", "#9400d3"],
  ["Dark Orchid", "#9932cc"],
  ["Medium Orchid", "#ba55d3"],
  ["Medium Purple", "#9370db"],
  ["Orchid", "#da70d6"],
  ["Violet", "#ee82ee"],
  ["Plum", "#dda0dd"],
  ["Thistle", "#d8bfd8"],
  ["Lavender", "#e6e6fa"],
  ["Magenta", "#ff00ff"],
  ["Deep Pink", "#ff1493"],
  ["Hot Pink", "#ff69b4"],
  ["Pale Violet Red", "#db7093"],
  ["Crimson", "#dc143c"],
  ["Pink", "#ffc0cb"],
  ["Light Pink", "#ffb6c1"],
  ["Salmon", "#fa8072"],
  ["Light Salmon", "#ffa07a"],
  ["Dark Salmon", "#e9967a"],
  ["Peach Puff", "#ffdab9"],
  ["Misty Rose", "#ffe4e1"],
  ["Bisque", "#ffe4c4"],
  ["Wheat", "#f5deb3"],
  ["Beige", "#f5f5dc"],
  ["Ivory", "#fffff0"],
  ["Cornsilk", "#fff8dc"],
];

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (m) {
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgbMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(hex.trim());
  if (rgbMatch) {
    return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
  }
  return null;
}

const PALETTE_RGB = NAMED_COLORS.map(([name, hex]) => ({
  name,
  rgb: hexToRgb(hex) as [number, number, number],
}));

/** Returns the closest named color for a hex/rgb color string, or null if unparseable. */
export function nearestColorName(color: string): string | null {
  const rgb = hexToRgb(color);
  if (!rgb) return null;

  let best: { name: string; dist: number } | null = null;
  for (const candidate of PALETTE_RGB) {
    const dr = rgb[0] - candidate.rgb[0];
    const dg = rgb[1] - candidate.rgb[1];
    const db = rgb[2] - candidate.rgb[2];
    const dist = dr * dr + dg * dg + db * db;
    if (!best || dist < best.dist) best = { name: candidate.name, dist };
  }
  return best?.name ?? null;
}
